/**
 * Aggregate daily stats across multiple campaigns into the shapes the
 * dashboard needs (recent totals, sparklines, since-last-visit deltas).
 *
 * Pure functions — no React, no Firestore — so they're cheap to unit-test.
 *
 * Daily stats input shape (per campaign):
 *   [{ date: 'YYYY-MM-DD', plays, completions, ctas, emailCaptures, ... }]
 */

/**
 * Sum a single metric across the last N days, across every campaign.
 * Returns 0 when there's nothing to sum.
 */
export function sumMetricLastNDays(dailyByCampaign, metric, days) {
  if (!dailyByCampaign || days <= 0) return 0;
  const cutoff = isoDateNDaysAgo(days - 1); // inclusive of today
  let total = 0;
  for (const id in dailyByCampaign) {
    const series = dailyByCampaign[id] || [];
    for (const day of series) {
      if (day?.date && day.date >= cutoff) {
        total += Number(day[metric]) || 0;
      }
    }
  }
  return total;
}

/**
 * Build a 7-element array of daily totals for one metric, across all
 * campaigns. Index 0 = 6 days ago, index 6 = today. Days with no data
 * are zero. Useful for sparkline rendering.
 */
export function buildSparkline(dailyByCampaign, metric, days = 7) {
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const result = new Array(days).fill(0);
  if (!dailyByCampaign) return result;

  for (let i = 0; i < days; i++) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() - (days - 1 - i));
    const iso = d.toISOString().split('T')[0];
    let total = 0;
    for (const id in dailyByCampaign) {
      const series = dailyByCampaign[id] || [];
      const hit = series.find((s) => s?.date === iso);
      if (hit) total += Number(hit[metric]) || 0;
    }
    result[i] = total;
  }
  return result;
}

/**
 * Sum a metric across all campaigns from `sinceIsoDate` (inclusive) to today.
 * Used by "Since you last visited" — pass the user's previous visit date.
 *
 * If `sinceIsoDate` is null/missing, returns null (caller should hide the
 * delta UI rather than show a meaningless number).
 */
export function sumMetricSince(dailyByCampaign, metric, sinceIsoDate) {
  if (!sinceIsoDate || !dailyByCampaign) return null;
  let total = 0;
  for (const id in dailyByCampaign) {
    const series = dailyByCampaign[id] || [];
    for (const day of series) {
      if (day?.date && day.date >= sinceIsoDate) {
        total += Number(day[metric]) || 0;
      }
    }
  }
  return total;
}

/**
 * "Sunday" / "5 days ago" / "yesterday" / "today" friendly label for a delta.
 */
export function formatSinceLabel(sinceIsoDate, now = new Date()) {
  if (!sinceIsoDate) return null;
  const since = new Date(`${sinceIsoDate}T00:00:00Z`);
  const today = new Date(now);
  today.setUTCHours(0, 0, 0, 0);
  const diffDays = Math.round((today.getTime() - since.getTime()) / (24 * 60 * 60 * 1000));
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  // Format as e.g. "5 May" for older
  const opts = { day: 'numeric', month: 'short' };
  return since.toLocaleDateString('en-GB', { ...opts, timeZone: 'UTC' });
}

function isoDateNDaysAgo(daysAgo) {
  const d = new Date();
  d.setUTCHours(0, 0, 0, 0);
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return d.toISOString().split('T')[0];
}
